import { useState } from 'react';

export const FLEET_ACCESS_RANKING_STORAGE_KEY = 'fleet_switcher:accessRanking';

/** Quantas frotas o ranking guarda. Acima disso, as menos recentes são descartadas. */
const MAX_STORED_FLEETS = 50;

export interface FleetAccessEntry {
  count: number;
  lastAccessedAt: string;
}

export type FleetAccessRanking = Record<string, FleetAccessEntry>;

const parseTimestamp = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const isValidEntry = (value: unknown): value is FleetAccessEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<FleetAccessEntry>;
  const hasValidCount = typeof entry.count === 'number' && Number.isFinite(entry.count) && entry.count >= 0;
  return hasValidCount && parseTimestamp(entry.lastAccessedAt) !== null;
};

const readRanking = (): FleetAccessRanking => {
  try {
    if (typeof window === 'undefined') return {};

    const stored = window.localStorage.getItem(FLEET_ACCESS_RANKING_STORAGE_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    return Object.entries(parsed).reduce<FleetAccessRanking>((acc, [fleetId, entry]) => {
      if (isValidEntry(entry)) acc[fleetId] = { count: entry.count, lastAccessedAt: entry.lastAccessedAt };
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const writeRanking = (ranking: FleetAccessRanking) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(FLEET_ACCESS_RANKING_STORAGE_KEY, JSON.stringify(ranking));
  } catch {
    // storage cheio / indisponível — ranking é best-effort
  }
};

const getLastAccessedTime = (entry?: FleetAccessEntry) => parseTimestamp(entry?.lastAccessedAt) ?? 0;

const trimRanking = (ranking: FleetAccessRanking): FleetAccessRanking => {
  const fleetIds = Object.keys(ranking);
  if (fleetIds.length <= MAX_STORED_FLEETS) return ranking;

  return fleetIds
    .sort((a, b) => getLastAccessedTime(ranking[b]) - getLastAccessedTime(ranking[a]))
    .slice(0, MAX_STORED_FLEETS)
    .reduce<FleetAccessRanking>((acc, fleetId) => {
      acc[fleetId] = ranking[fleetId];
      return acc;
    }, {});
};

/**
 * Ranking de frotas mais acessadas, persistido no localStorage.
 *
 * A ordenação usa nº de acessos (desc), acesso mais recente (desc) e nome (asc) como desempate,
 * de forma que frotas nunca acessadas mantêm a ordem alfabética original.
 *
 * @returns `sortFleetsByAccess` para ordenar a lista exibida e `trackFleetAccess` para registrar um acesso.
 */
export const useFleetAccessRanking = () => {
  const [ranking, setRanking] = useState<FleetAccessRanking>(readRanking);

  const sortFleetsByAccess = <T extends { id: string; name: string }>(fleets: T[]): T[] =>
    [...fleets].sort((a, b) => {
      const countDiff = (ranking[b.id]?.count ?? 0) - (ranking[a.id]?.count ?? 0);
      if (countDiff !== 0) return countDiff;

      const recencyDiff = getLastAccessedTime(ranking[b.id]) - getLastAccessedTime(ranking[a.id]);
      if (recencyDiff !== 0) return recencyDiff;

      return a.name.localeCompare(b.name);
    });

  const trackFleetAccess = (fleetId: string) => {
    if (!fleetId) return;

    const currentRanking = readRanking();
    const nextRanking = trimRanking({
      ...currentRanking,
      [fleetId]: {
        count: (currentRanking[fleetId]?.count ?? 0) + 1,
        lastAccessedAt: new Date().toISOString(),
      },
    });

    writeRanking(nextRanking);
    setRanking(nextRanking);
  };

  return { ranking, sortFleetsByAccess, trackFleetAccess };
};

export default useFleetAccessRanking;
